import {
  IMG, CONTACT, STATS, COMPANY_INTRO, MISSION, VISION, VALUES,
  TIMELINE,
  STOCKINGS, COMPLEMENTS, PRODUCT_GROUPS,
} from "@/lib/site";
import { CERT_STANDARD } from "@/lib/certifications";
import { Stethoscope, Ruler, HeartHandshake, Award, BadgeCheck, MapPin, Target, Eye, Bandage, Waves, FileText, ListChecks, CheckCircle2, Heart, Check, ArrowRight, ArrowDown, Gauge, MonitorSmartphone, Split, Home, Warehouse, Truck, ShieldCheck, Network, Boxes, RefreshCw, Cross, Store, Building2, UserRound, Phone, Mail, Globe } from "lucide-react";

const PRINT_CSS = `
  @page { size: A4; margin: 0; }
  html, body { background: #fff; }
  .pdf-doc { font-family: 'Manrope', system-ui, sans-serif; color: #1B3A6B; }
  .sheet {
    position: relative; width: 210mm; height: 297mm; margin: 0 auto;
    padding: 16mm 15mm 20mm; background: #fff; overflow: hidden;
    page-break-after: always; break-after: page;
    display: flex; flex-direction: column;
  }
  .sheet:last-child { page-break-after: auto; break-after: auto; }
  .sheet-foot {
    position: absolute; left: 15mm; right: 15mm; bottom: 11mm;
    display: flex; align-items: center; justify-content: space-between;
    border-top: 1px solid #EEF3F6; padding-top: 9px;
    font-size: 8.5px; letter-spacing: 0.15em; text-transform: uppercase; color: rgba(27,58,107,.4);
  }
  img { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
`;

const Foot = ({ n }) => (
  <div className="sheet-foot">
    <span>Medisistem · Company Profile</span>
    <span>{CONTACT.web}</span>
    <span>{String(n).padStart(2, "0")}</span>
  </div>
);

const Head = ({ label }) => (
  <div className="flex items-center justify-between border-b border-[#E7EDF2] pb-4 mb-8">
    <img src="/brands/medisistem_color.png" alt="Medisistem" className="h-7 w-auto" />
    <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-navy/40">{label}</span>
  </div>
);

const Eyebrow = ({ children }) => <span className="eyebrow">{children}</span>;
const Title = ({ children, className = "" }) => (
  <h2 className={`mt-4 font-serif text-[30px] leading-[1.12] font-bold text-navy ${className}`}>{children}</h2>
);
const Accent = () => <div className="mt-4 h-1 w-14 rounded-full bg-green" />;

const Chip = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-green/25 bg-green/5 px-3.5 py-1.5 text-[11px] font-semibold text-green-deep">{children}</span>
);

const BendaggioCard = ({ f }) => (
  <div className="flex flex-col overflow-hidden rounded-xl border border-[#E7EDF2]">
    <img src={f.img} alt={f.title} className="h-[26mm] w-full object-cover object-center" />
    <div className="flex flex-1 flex-col p-3.5">
      <div className="font-serif text-[12.5px] font-bold leading-[1.15] text-navy">{f.title}</div>
      <p className="mt-1.5 text-[8.5px] leading-[1.4] text-muted-foreground">{f.desc}</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {f.tags.map((t) => (
          <span key={t} className="rounded-full bg-green/10 px-2 py-[3px] text-[7px] font-semibold uppercase tracking-[0.06em] text-green-deep">{t}</span>
        ))}
      </div>
    </div>
  </div>
);

const SOLUTION_BG = { navy: "#12284C", green: "#1F5C48", "green-alt": "#0E7A5F" };
const SOLUTION_GLOW = { navy: "#1B3A6B", green: "#2E8B6B", "green-alt": "#15B58C" };
const COMPLEMENT_ICONS = { bendaggi: Bandage, pressoterapia: Waves };
const LUMAVEN_IMG = { "lum-prev": "/products/lumaven-preventive/collant.jpg", "lum-anti": "/products/lumaven-antitrombo/coscia.jpg" };
const RAL_TEXT = "Il marchio di qualità RAL identifica gli specifici dispositivi compressivi medicali che hanno ottenuto la relativa certificazione secondo i requisiti previsti. Il marchio è riferito esclusivamente ai singoli prodotti certificati e non indistintamente all'intera gamma SCHIEBLER.";
const SCHIEBLER_FAMILIES = [
  { img: "/products/star-cotton/star-cotton-petrolio-1.webp", title: "Calze e indumenti compressivi", desc: "Calze e indumenti a compressione medicale disponibili in differenti classi, modelli, materiali ed esecuzioni, secondo indicazione sanitaria.", brands: "STAR COTTON · GOLDPUNKT · DOKTUS" },
  { img: "/products/easywrap-strong-knee/easywrap-strong-knee-sabbia-1.webp", title: "Sistemi compressivi adattivi", desc: "Sistemi regolabili progettati per adattare la compressione alle caratteristiche della persona e agevolare l'applicazione e la gestione quotidiana." },
  { img: "/products/ortesi-tutori/air-soft-splint-1.webp", title: "Ortesi e tutori", desc: "Dispositivi di supporto destinati alla protezione, alla stabilizzazione e al sostegno di specifici distretti corporei." },
  { img: "/products/accessori/arion-easy-slide-1.webp", title: "Accessori", desc: "Prodotti complementari pensati per facilitare l'applicazione, l'utilizzo e la gestione quotidiana dei dispositivi SCHIEBLER." },
];
const SCHIEBLER_VALUES = ["Distribuzione esclusiva nazionale", "Accesso alla gamma SCHIEBLER", "Supporto tecnico-informativo"];

const BENDAGGI_FAMILIES = [
  { img: "/products/bendaggi/wero-swiss-adesiva.webp", title: "Bende adesive", desc: "Bende destinate al supporto muscolare, tendineo e articolare e al fissaggio delle medicazioni. La massa adesiva porosa e traspirante favorisce stabilità, adattabilità e comfort durante l'applicazione.", tags: ["Supporto muscolare e articolare", "Fissaggio delle medicazioni", "Massa adesiva porosa e traspirante"] },
  { img: "/products/bendaggi/wero-swiss-lan-cohesive.webp", title: "Bende coesive", desc: "Bende antiscivolo che aderiscono su sé stesse senza aderire direttamente alla pelle. Utilizzate nella fase di decongestione della terapia compressiva e nelle applicazioni che richiedono stabilità e tenuta nel tempo.", tags: ["Aderenza su sé stesse", "Stabilità del bendaggio", "Supporto nella fase di decongestione"] },
  { img: "/products/bendaggi/wero-swiss-forte.webp", title: "Bende non adesive", desc: "Bende elastiche disponibili a corta, media e forte elasticità, utilizzate nella terapia compressiva, in ortopedia e nella medicina dello sport. Le differenti strutture rispondono a diverse modalità di applicazione.", tags: ["Elasticità corta, media o forte", "Terapia compressiva", "Ortopedia e medicina dello sport"] },
  { img: "/products/bendaggi/benda-ossido-di-zinco.jpg", title: "Bende all'ossido di zinco", desc: "Medicazioni elastiche impregnate di pasta all'ossido di zinco, con proprietà lenitive e dermoprotettive. Pensate per offrire un contatto morbido e uniforme durante l'applicazione.", tags: ["Pasta all'ossido di zinco", "Azione lenitiva", "Proprietà dermoprotettive"] },
  { img: "/products/bendaggi/benda-alginato.jpg", title: "Bende all'alginato", desc: "Medicazioni avanzate in fibre di alginato che, a contatto con l'essudato, si trasformano in gel. Destinate alla gestione delle ferite essudanti secondo le indicazioni del personale sanitario.", tags: ["Fibre di alginato", "Trasformazione in gel", "Gestione delle ferite essudanti"] },
];
const BENDAGGI_NOTE = "Ogni tipologia risponde a una funzione specifica. La scelta del bendaggio deve essere effettuata in base alla finalità di utilizzo, alla fase del trattamento e alle indicazioni del personale sanitario.";

const PRESSO_SPECS = [
  { icon: Split, title: "4 uscite", desc: "Gestione degli applicatori previsti." },
  { icon: Gauge, title: "Pressione regolabile", desc: "Intervallo 20–250 mmHg." },
  { icon: MonitorSmartphone, title: "Display touch-screen", desc: "Regolazione e gestione delle sezioni." },
  { icon: Waves, title: "Programmi A e B", desc: "Due modalità di compressione sequenziale." },
];
const PRESSO_AMBITI = [
  "Linfedema primario o secondario",
  "Lipedema",
  "Insufficienza venosa cronica",
  "Prevenzione del ristagno linfatico",
  "Edema post-traumatico o post-chirurgico",
  "Recupero e benessere degli arti inferiori",
];

const SERVICES_PROFILE = [
  { icon: Stethoscope, title: "Consulenza e supporto specialistico", desc: "Un confronto dedicato per orientare la persona o l'operatore tra le soluzioni disponibili, in coerenza con la prescrizione e con le indicazioni ricevute." },
  { icon: Ruler, title: "Misurazione, prova e assistenza", desc: "Rilevazione professionale delle misure, prova della calza e supporto successivo per favorire vestibilità, comfort e corretta gestione del dispositivo." },
  { icon: Warehouse, title: "Stock e magazzino", desc: "Gestione organizzata delle disponibilità e dei riassortimenti, per favorire continuità nelle forniture nel rispetto delle tempistiche previste per ciascun prodotto." },
  { icon: Truck, title: "Logistica e consegne", desc: "Coordinamento degli ordini e delle consegne in base alla disponibilità dei prodotti e alle esigenze di privati, professionisti e strutture." },
  { icon: ShieldCheck, title: "Qualità e sicurezza dei prodotti", desc: "Attenzione alle certificazioni, alla documentazione tecnica e alla corretta identificazione dei dispositivi distribuiti, nel rispetto delle caratteristiche dichiarate dai produttori." },
  { icon: Network, title: "Esperienza multicanale", desc: "Competenze sviluppate nei rapporti con privati, professionisti sanitari, strutture e canali specializzati, con modalità di servizio adeguate ai diversi contesti." },
];
const SERVICES_INDICATORS = [
  { icon: Home, label: "Servizio in sede e a domicilio" },
  { icon: Boxes, label: "Gestione stock e forniture" },
  { icon: Truck, label: "Logistica organizzata" },
];
const CERT_PILLARS = [
  { icon: ListChecks, title: "Processi controllati", desc: "Attività organizzate attraverso procedure definite, verifiche interne e responsabilità chiaramente individuate." },
  { icon: HeartHandshake, title: "Attenzione al cliente", desc: "Ascolto delle esigenze, gestione puntuale delle richieste e supporto nella fornitura delle soluzioni adeguate." },
  { icon: RefreshCw, title: "Miglioramento continuo", desc: "Valutazione periodica delle attività e aggiornamento dei processi per rendere il servizio sempre più efficace, affidabile e coerente." },
];
const CHANNELS_PROFILE = [
  { icon: Cross, title: "Farmacie", desc: "Supporto nella disponibilità di calze, dispositivi compressivi e prodotti specialistici, con informazioni tecniche e indicazioni per una corretta gestione delle richieste del cliente." },
  { icon: Store, title: "Sanitarie e ortopedie", desc: "Una gamma articolata di prodotti LUMAVEN e SCHIEBLER, bendaggi e altre soluzioni, accompagnata da supporto tecnico-informativo nella gestione di modelli, misure ed esecuzioni." },
  { icon: Truck, title: "Distributori farmaceutici", desc: "Collaborazione con gli operatori della distribuzione intermedia farmaceutica che riforniscono farmacie e canali sanitari specializzati, con una gestione organizzata di ordini, disponibilità e forniture." },
  { icon: Stethoscope, title: "Medici specialisti", desc: "Un riferimento informativo per flebologi, angiologi, chirurghi vascolari e altri specialisti coinvolti nei percorsi di terapia elasto-compressiva, nel pieno rispetto delle competenze cliniche." },
  { icon: Building2, title: "Strutture sanitarie", desc: "Forniture dedicate a ospedali, cliniche, RSA e strutture assistenziali, con soluzioni comprendenti calze antitrombo, bendaggi, sistemi di pressoterapia e altri dispositivi specialistici." },
  { icon: UserRound, title: "Pazienti", desc: "Misurazione professionale, prova della calza e assistenza in sede o a domicilio, nel rispetto della prescrizione ricevuta e delle indicazioni del personale sanitario." },
];




const METHOD_FLOW = [
  { icon: FileText, title: "Prescrizione dello specialista", desc: "Il medico indica la classe di compressione più adatta alla condizione clinica." },
  { icon: Ruler, title: "Misurazione professionale", desc: "Rileviamo circonferenze e lunghezze con precisione, punto per punto." },
  { icon: ListChecks, title: "Scelta della taglia corretta", desc: "Individuiamo la misura esatta, non soltanto la classe di compressione." },
  { icon: CheckCircle2, title: "Prova della calza", desc: "La persona prova la calza: verifichiamo vestibilità e comfort reali." },
  { icon: Heart, title: "Maggiore comfort ed efficacia terapeutica", desc: "Una calza correttamente misurata può favorire l'aderenza e la continuità del percorso terapeutico indicato." },
];

const METHOD_VANTAGGI = [
  "Corretta vestibilità",
  "Maggiore efficacia della terapia",
  "Più comfort nella vita quotidiana",
  "Riduzione dei resi e delle sostituzioni",
  "Maggiore soddisfazione della persona",
  "Maggiore fiducia nel trattamento e nel prodotto",
];

const CHI_SIAMO_POINTS = [
  [Award, "Marchio di proprietà", "LUMAVEN nasce e cresce con noi: controllo totale sulla qualità."],
  [BadgeCheck, "Distributore esclusivo", "SCHIEBLER per l'Italia: eccellenza tedesca certificata."],
  [Ruler, "Misurazione professionale", "La misura giusta fa la differenza sull'efficacia terapeutica."],
  [MapPin, "In sede o a domicilio", "Ti raggiungiamo dove sei, con la stessa cura e precisione."],
];

const WhatsappIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.413c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.187zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.767.967-.94 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
  </svg>
);

const CompanyProfilePrint = () => (
  <div className="pdf-doc">
    <style>{PRINT_CSS}</style>

    {/* 01 — COVER (blu con foto affiancata) */}
    <section className="sheet" style={{ background: "#12284C", color: "#fff", padding: 0, overflow: "hidden" }}>
      <div className="grid h-full" style={{ gridTemplateColumns: "1fr 78mm" }}>
        {/* colonna testo */}
        <div className="relative flex flex-col justify-between" style={{ padding: "18mm 8mm 22mm 13mm" }}>
          <div className="absolute inset-0 grain-overlay" />
          {/* gruppo alto: logo + titolo ravvicinati */}
          <div className="relative">
            <div className="inline-flex rounded-2xl bg-white px-6 py-5 shadow-[0_16px_40px_-18px_rgba(0,0,0,0.55)] ring-1 ring-white/60">
              <img src="/brands/medisistem_color.png" alt="Medisistem" className="h-[66px] w-auto" />
            </div>
            <h1 className="mt-9 font-serif text-[48px] leading-[1.06] font-bold text-white tracking-tight">
              La terapia<br />elasto-compressiva<br />ha un centro<br /><span className="text-green">di riferimento.</span>
            </h1>
            <p className="mt-6 font-serif italic text-[29px] text-green">Affidabilità che si indossa.</p>
          </div>
          {/* blocco posizionamento istituzionale (sintesi, non è una CTA) */}
          <div className="relative">
            {[
              [Stethoscope, "Competenza nell'elasto-compressione"],
              [Boxes, "Gamma specialistica selezionata"],
              [Network, "Distribuzione e supporto multicanale"],
            ].map(([Icon, label], i) => (
              <div key={label} className={`flex items-center gap-4 py-3 ${i > 0 ? "border-t border-white/10" : ""}`}>
                <Icon size={20} strokeWidth={1.6} className="shrink-0 text-green" />
                <span className="text-[12.5px] font-semibold uppercase tracking-[0.17em] text-white/85">{label}</span>
              </div>
            ))}
          </div>
          {/* gruppo basso: indicatori + dati istituzionali */}
          <div className="relative">
            <div className="grid grid-cols-3 gap-3" data-testid="cover-stats">
              {STATS.map((s) => (
                <div key={s.label} data-testid={`cover-stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`} className="rounded-xl border border-white/15 px-4 py-4">
                  <div data-testid="cover-stat-value" className={`font-serif font-bold text-green leading-none whitespace-nowrap ${/\d/.test(s.value) ? "text-[27px]" : "text-[19px]"}`}>{s.value}</div>
                  <div className="mt-2 text-[8.5px] font-semibold uppercase tracking-[0.14em] text-white/70 leading-tight">{s.label}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-end justify-between">
              <div className="text-[12px] uppercase tracking-[0.16em] text-white/55 leading-[2]">
                {CONTACT.since} · Napoli<br />{CONTACT.web}
              </div>
              <div className="mb-1 h-1 w-14 rounded-full bg-green" />
            </div>
          </div>
        </div>
        {/* colonna foto */}
        <div className="relative h-full">
          <img src={IMG.heroLegs} alt="Terapia elasto-compressiva Medisistem" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(105deg, rgba(18,40,76,0.55) 0%, rgba(18,40,76,0) 40%)" }} />
        </div>
      </div>
    </section>

    {/* 02 — CHI SIAMO */}
    <section className="sheet">
      <Head label="Chi siamo" />
      <Eyebrow>La nostra identità</Eyebrow>
      <Title>Oltre quarant&apos;anni dedicati<br />al benessere della tua circolazione.</Title>
      <Accent />
      <div className="mt-7 grid grid-cols-[1.15fr_1fr] gap-8 items-start">
        <div className="space-y-3.5 text-[12.5px] leading-relaxed text-muted-foreground">
          {COMPANY_INTRO.map((p, i) => <p key={i}>{p}</p>)}
          <p className="pt-1 font-serif text-lg italic text-green">{CONTACT.payoff}</p>
        </div>
        <div className="overflow-hidden rounded-2xl border border-[#E7EDF2]">
          <img src={IMG.consultation} alt="Consulenza Medisistem" className="h-[86mm] w-full object-cover" />
        </div>
      </div>
      <div className="mt-auto pt-8">
        <Eyebrow>Perché sceglierci</Eyebrow>
        <div className="mt-4 grid grid-cols-2 gap-4">
          {CHI_SIAMO_POINTS.map(([Icon, title, desc]) => (
            <div key={title} className="flex items-start gap-4 rounded-2xl border border-[#E7EDF2] bg-cloud/60 px-5 py-4">
              <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-green/10 text-green">
                <Icon size={20} strokeWidth={1.7} />
              </span>
              <div>
                <div className="font-serif text-[15px] font-bold text-navy leading-tight">{title}</div>
                <p className="mt-1 text-[10.5px] leading-[1.45] text-muted-foreground">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Foot n={2} />
    </section>

    {/* 03 — STORIA / TIMELINE */}
    <section className="sheet">
      <Head label="La nostra storia" />
      <Eyebrow>Il percorso</Eyebrow>
      <Title>Una storia di esperienza<br />e continuità.</Title>
      <Accent />
      <p className="mt-5 text-[12.5px] font-semibold uppercase tracking-[0.18em] text-navy/45">Dal 1986 ad oggi</p>
      <div className="relative mt-8 flex-1 pl-8">
        <div className="absolute left-[7px] top-2 bottom-2 w-px bg-navy/15" />
        <div className="flex h-full flex-col justify-between">
          {TIMELINE.map((t) => (
            <div key={t.year} className="relative">
              <span className="absolute -left-8 top-1 flex h-4 w-4 items-center justify-center">
                <span className="h-4 w-4 rounded-full border-2 border-green bg-white" />
                <span className="absolute h-2 w-2 rounded-full bg-green" />
              </span>
              <div className="font-serif text-[18px] font-bold text-navy leading-none">{t.year}</div>
              <div className="mt-2.5 rounded-xl border border-[#E7EDF2] bg-white p-4 shadow-sm">
                <p className="text-[11px] leading-[1.5] text-muted-foreground">{t.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Foot n={3} />
    </section>

    {/* 04 — MISSIONE, VISIONE, VALORI */}
    <section className="sheet">
      <Head label="Missione · Visione · Valori" />
      <Eyebrow>Perché lo facciamo</Eyebrow>
      <Title>La persona al centro,<br />prima del prodotto.</Title>
      <Accent />
      <div className="mt-7 grid grid-cols-2 gap-4">
        <div className="rounded-2xl bg-navy p-6 text-white">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-green"><Target size={18} /></span>
            <h3 className="font-serif text-[17px] font-bold">Missione</h3>
          </div>
          <p className="mt-3 text-[11.5px] leading-relaxed text-white/80">{MISSION}</p>
        </div>
        <div className="rounded-2xl bg-green-deep p-6 text-white">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/15 text-white"><Eye size={18} /></span>
            <h3 className="font-serif text-[17px] font-bold">Visione</h3>
          </div>
          <p className="mt-3 text-[11.5px] leading-relaxed text-white/85">{VISION}</p>
        </div>
      </div>
      <div className="mt-7">
        <Eyebrow>I nostri valori</Eyebrow>
        <div className="mt-4 grid grid-cols-3 gap-4">
          {VALUES.map((v, i) => (
            <div key={v.title} className="rounded-2xl border border-[#E7EDF2] p-5">
              <span className="font-serif text-[26px] font-bold text-green/25 leading-none">0{i + 1}</span>
              <div className="mt-2 font-serif text-[16px] font-bold text-navy">{v.title}</div>
              <p className="mt-1.5 text-[11px] leading-[1.5] text-muted-foreground">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-auto flex items-center justify-between rounded-2xl bg-navy px-7 py-6 text-white">
        <div>
          <div className="font-serif text-[22px] font-bold">{CONTACT.payoff}</div>
          <p className="mt-1.5 text-[11.5px] text-white/70">La persona al centro, in ogni fase del percorso terapeutico.</p>
        </div>
        <div className="ml-6 h-1 w-16 shrink-0 rounded-full bg-green" />
      </div>
      <Foot n={4} />
    </section>

    {/* 05 — SOLUZIONI */}
    <section className="sheet">
      <Head label="Le soluzioni" />
      <Eyebrow>La nostra offerta</Eyebrow>
      <Title>Cinque soluzioni per ogni<br />fase del percorso di cura.</Title>
      <Accent />
      <div className="mt-6 flex flex-1 flex-col justify-between">
        <div className="space-y-2.5 text-[10.5px] leading-[1.6] text-muted-foreground">
          <p>Medisistem riunisce in un&apos;unica proposta soluzioni dedicate alla prevenzione, alla terapia e al completamento del percorso elasto-compressivo. La compressione terapeutica comprende calze medicali certificate, disponibili in differenti modelli e classi di compressione, destinate al trattamento dell&apos;insufficienza venosa cronica e del linfedema secondo prescrizione o indicazione sanitaria.</p>
          <p>Le linee preventive sono pensate per offrire sostegno e comfort nella vita quotidiana, in particolare alle persone che trascorrono molte ore in piedi o sedute, durante i viaggi e nei periodi in cui le gambe possono essere maggiormente affaticate. Le calze antitrombo sono invece dedicate alla profilassi antitromboembolica nei contesti ospedalieri, nel periodo post-operatorio e, quando indicato dal personale sanitario, anche a domicilio.</p>
          <p>A completamento dell&apos;offerta, Medisistem propone bendaggi per la terapia compressiva e sistemi di pressoterapia per il drenaggio linfatico sequenziale. Soluzioni differenti, ma parte di un unico approccio fondato sulla qualità dei dispositivi, sulla corretta rilevazione delle misure e sull&apos;assistenza professionale.</p>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {STOCKINGS.map((s) => (
            <div
              key={s.id}
              className={`relative flex min-h-[52mm] flex-col overflow-hidden rounded-3xl p-6 ${s.featured ? "ring-2 ring-green" : ""}`}
              style={{ background: SOLUTION_BG[s.theme] }}
            >
              <div className="absolute -bottom-12 -right-12 h-40 w-40 rounded-full opacity-40 blur-[70px]" style={{ background: SOLUTION_GLOW[s.theme] }} />
              <div className="relative">
                {s.featured && (
                  <span className="mb-3 inline-flex items-center rounded-full border border-white/25 bg-white/15 px-3 py-1 text-[9px] font-bold uppercase tracking-wider text-white">La più scelta</span>
                )}
                <span className="block text-[9.5px] font-semibold uppercase tracking-[0.16em] text-white/70">{s.subtitle}</span>
                <h3 className="mt-2 font-serif text-[19px] font-bold leading-tight text-white">{s.title}</h3>
                <p className="mt-2.5 text-[10px] leading-[1.5] text-white/75">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div>
          <div className="flex items-center gap-4">
            <span className="h-px flex-1 bg-[#E7EDF2]" />
            <span className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">A completamento della terapia</span>
            <span className="h-px flex-1 bg-[#E7EDF2]" />
          </div>
          <div className="mt-5 grid grid-cols-2 gap-4">
            {COMPLEMENTS.map((c) => {
              const Icon = COMPLEMENT_ICONS[c.id] || Bandage;
              return (
                <div key={c.id} className="flex items-center gap-5 rounded-2xl border border-[#E7EDF2] bg-cloud px-6 py-5">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-green/12 text-green-deep"><Icon size={22} /></span>
                  <div>
                    <h4 className="font-serif text-[17px] font-bold text-navy">{c.label}</h4>
                    <p className="mt-1.5 text-[10.5px] leading-[1.5] text-muted-foreground">{c.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex items-center gap-5 rounded-2xl border border-green/20 bg-green/5 px-7 py-5">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green/12 text-green-deep"><Ruler size={21} /></span>
          <p className="text-[10.5px] leading-[1.6] text-navy/80">
            <span className="font-semibold text-navy">Il personale Medisistem accompagna ogni persona</span> nell&apos;individuazione del modello, dell&apos;esecuzione e della taglia più adatti alle indicazioni ricevute. Il servizio di misurazione professionale è disponibile sia presso la sede sia a domicilio, per favorire una vestibilità corretta, un utilizzo più confortevole e la necessaria continuità del percorso.
          </p>
        </div>
      </div>
      <Foot n={5} />
    </section>

    {/* 06 — IL METODO · IL SERVIZIO DI MISURAZIONE */}
    <section className="sheet">
      <Head label="Il metodo Medisistem" />
      <div className="flex items-center justify-between">
        <Eyebrow>Il servizio di misurazione</Eyebrow>
        <span className="inline-flex items-center rounded-full border border-green/30 bg-green/5 px-3.5 py-1.5 text-[9px] font-bold uppercase tracking-[0.15em] text-green-deep">Il metodo · 1 di 2</span>
      </div>
      <Title>Perché la misura conta<br />più di quanto si creda.</Title>
      <Accent />
      <div className="mt-7 grid flex-1 grid-cols-[0.82fr_1.18fr] items-stretch gap-8">
        <div className="overflow-hidden rounded-2xl border border-[#E7EDF2]">
          <img src={IMG.measure} alt="Servizio di misurazione professionale Medisistem" className="h-full w-full object-cover" />
        </div>
        <div className="flex flex-col justify-center">
          <div className="space-y-4 text-[11.5px] leading-[1.65] text-muted-foreground">
            <p>La classe di compressione viene indicata dallo specialista in base alla condizione clinica del paziente. Tuttavia, per scegliere correttamente una calza elasto-compressiva, non è sufficiente conoscere soltanto la classe prescritta: è necessario individuare anche la taglia, il modello e l&apos;esecuzione più adatti alle caratteristiche della persona.</p>
            <p>Circonferenze e lunghezze devono essere rilevate con precisione in punti specifici della gamba. Una misura non corretta può determinare una calza troppo stretta, troppo larga o poco stabile, rendendola meno confortevole e più difficile da utilizzare con continuità nella vita quotidiana.</p>
            <p>Dopo la prescrizione dello specialista, Medisistem effettua la <span className="font-semibold text-navy">misurazione professionale</span>, individua la taglia corretta e consente di provare la calza per verificarne la vestibilità e il comfort reali. Il servizio è svolto da personale qualificato ed è disponibile sia presso la sede Medisistem sia a domicilio.</p>
            <p>Questo passaggio permette di accompagnare la persona nella scelta del dispositivo più coerente con la prescrizione ricevuta e con le proprie misure, senza sostituire in alcun modo la valutazione o l&apos;indicazione del medico.</p>
          </div>
        </div>
      </div>
      <div className="relative mt-6 overflow-hidden rounded-2xl px-8 py-5 text-white" style={{ background: "linear-gradient(135deg,#1F5C48 0%,#175340 60%,#12362b 100%)" }}>
        <div className="absolute inset-0 grain-overlay" />
        <div className="relative">
          <span className="font-serif text-[34px] leading-none text-green">&ldquo;</span>
          <blockquote className="-mt-2 font-serif text-[13.5px] font-medium leading-[1.5] text-white/95">
            La qualità di una calza elasto-compressiva non dipende soltanto dal prodotto, ma anche dalla corretta scelta della misura. Per questo Medisistem affianca ogni persona con un servizio professionale di misurazione e prova, trasformando la scelta della calza in un percorso preciso e consapevole.
          </blockquote>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-end gap-2 text-[9.5px] font-bold uppercase tracking-[0.15em] text-green-deep">
        <span>Continua: il metodo in cinque passaggi</span>
        <ArrowRight size={13} />
      </div>
      <Foot n={6} />
    </section>

    {/* 07 — IL METODO · I PASSAGGI */}
    <section className="sheet">
      <Head label="Il metodo Medisistem" />
      <div className="flex items-center justify-between">
        <Eyebrow>Il metodo Medisistem</Eyebrow>
        <span className="inline-flex items-center rounded-full border border-green/30 bg-green/5 px-3.5 py-1.5 text-[9px] font-bold uppercase tracking-[0.15em] text-green-deep">Il metodo · 2 di 2</span>
      </div>
      <Title>Un percorso semplice,<br />in cinque passaggi.</Title>
      <Accent />
      <p className="mt-4 max-w-[160mm] text-[11.5px] leading-relaxed text-muted-foreground">
        Dalla prescrizione dello specialista alla prova della calza, ogni passaggio contribuisce a individuare il dispositivo più adatto alle indicazioni ricevute e alle misure della persona.
      </p>
      <div className="mt-6 flex flex-1 items-center">
        <div className="grid w-full grid-cols-[1.12fr_0.88fr] items-start gap-7">
          <div>
            {METHOD_FLOW.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title}>
                  <div className="flex items-center gap-4 rounded-2xl border border-[#E7EDF2] bg-white p-4 shadow-sm">
                    <span className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-green/10 text-green">
                      <Icon size={20} />
                      <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-green text-[10px] font-bold text-white">{i + 1}</span>
                    </span>
                    <div>
                      <h3 className="font-serif text-[14px] font-bold leading-snug text-navy">{s.title}</h3>
                      <p className="mt-0.5 text-[10px] leading-[1.45] text-muted-foreground">{s.desc}</p>
                    </div>
                  </div>
                  {i < METHOD_FLOW.length - 1 && (
                    <div className="flex justify-center py-1.5 text-green/50"><ArrowDown size={18} /></div>
                  )}
                </div>
              );
            })}
          </div>
          <div>
            <div className="rounded-3xl bg-navy p-6 text-white">
              <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-green">Perché è importante?</span>
              <h3 className="mt-3 font-serif text-[19px] font-bold leading-tight">I vantaggi di una misura corretta.</h3>
              <ul className="mt-4 space-y-3">
                {METHOD_VANTAGGI.map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green text-white"><Check size={11} strokeWidth={3} /></span>
                    <span className="text-[11px] leading-snug text-white/90">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-4 rounded-2xl border-l-4 border-green bg-green/5 py-4 pl-5 pr-5">
              <p className="font-serif text-[13.5px] leading-[1.4] text-navy">
                La prescrizione definisce la classe di compressione. <span className="text-green-deep">La misurazione professionale permette di individuare la taglia corretta.</span>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Foot n={7} />
    </section>

    {/* 08 — MARCHIO LUMAVEN */}
    <section className="sheet">
      <Head label="Marchi · LUMAVEN" />
      <div className="flex items-start justify-between gap-6">
        <div>
          <Eyebrow>Marchio di proprietà Medisistem</Eyebrow>
          <Title>LUMAVEN — prevenzione<br />e profilassi antitrombo.</Title>
          <Accent />
        </div>
        <img src="/brands/lumaven.png" alt="LUMAVEN" className="mt-1 h-[52px] w-auto object-contain" />
      </div>
      <p className="mt-5 max-w-[168mm] text-[11.5px] leading-relaxed text-muted-foreground">
        La linea di proprietà Medisistem dedicata alla prevenzione e alla profilassi antitrombo. Qualità controllata in ogni fase, dalla progettazione all&apos;assistenza.
      </p>
      <div className="mt-6 grid grid-cols-2 gap-5">
        {PRODUCT_GROUPS.find((g) => g.id === "lumaven").items.map((it) => (
          <div key={it.id} className="flex flex-col overflow-hidden rounded-2xl border border-[#E7EDF2]">
            <img src={LUMAVEN_IMG[it.id] || it.img} alt={it.name} className="h-[54mm] w-full object-cover" />
            <div className="p-5">
              <div className="font-serif text-[18px] font-bold text-navy">{it.name}</div>
              <p className="mt-2 text-[11px] leading-[1.5] text-muted-foreground">{it.desc}</p>
              {it.specs && (
                <div className="mt-3 flex flex-wrap gap-2">{it.specs.map((sp) => <Chip key={sp}>{sp}</Chip>)}</div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-auto flex items-center gap-6 rounded-2xl bg-navy px-8 py-6 text-white">
        <div>
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-green">La gamma LUMAVEN</span>
          <p className="mt-2 text-[11px] leading-[1.6] text-white/80">
            La gamma LUMAVEN nasce per accompagnare il benessere quotidiano delle gambe attraverso soluzioni confortevoli, curate nell&apos;estetica e adatte a differenti esigenze di vestibilità. Le linee 70 e 140 comprendono diverse esecuzioni, dai gambaletti ai collant, mentre la famiglia Comfort propone calzini dall&apos;aspetto discreto, pensati soprattutto per il pubblico maschile ma utilizzabili anche dalla donna.
          </p>
        </div>
      </div>
      <Foot n={8} />
    </section>

    {/* 09 — MARCHIO SCHIEBLER */}
    <section className="sheet">
      <Head label="Marchi · SCHIEBLER" />
      <div className="flex items-start justify-between gap-6">
        <div>
          <Eyebrow>Marchio distribuito</Eyebrow>
          <Title className="mt-2.5 text-[23px]">SCHIEBLER — esperienza tedesca<br />nella compressione medicale.</Title>
        </div>
        <img src="/brands/schiebler.png" alt="SCHIEBLER" className="mt-1 h-[40px] w-auto object-contain" />
      </div>

      {/* Banner blu Partnership Nazionale */}
      <div className="mt-3.5 flex items-center gap-5 rounded-2xl bg-navy px-7 py-3 text-white">
        <span className="inline-flex shrink-0 items-center rounded-full bg-green px-3.5 py-1.5 text-[9px] font-bold uppercase tracking-[0.16em] text-white">Partnership Nazionale</span>
        <div className="h-8 w-px bg-white/20" />
        <h3 className="font-serif text-[15px] font-bold leading-[1.2]">MEDISISTEM è distributore esclusivo SCHIEBLER per tutta l&apos;Italia.</h3>
      </div>

      <div className="mt-3 space-y-1.5 text-[9px] leading-[1.5] text-muted-foreground">
        <p>SCHIEBLER è uno storico marchio tedesco specializzato nella terapia elasto-compressiva e nello sviluppo di dispositivi destinati a differenti esigenze terapeutiche e di supporto. La gamma si estende dalle calze e dagli indumenti a compressione medicale ai sistemi compressivi adattivi, fino a ortesi, tutori e accessori dedicati alla gestione quotidiana dei dispositivi.</p>
        <p>La disponibilità di modelli, materiali, misure ed esecuzioni differenti permette di individuare soluzioni coerenti con le indicazioni ricevute e con le caratteristiche della persona. MEDISISTEM cura la distribuzione esclusiva SCHIEBLER per tutta l&apos;Italia, garantendo accesso alla gamma, supporto tecnico-informativo e continuità nella gestione delle forniture.</p>
      </div>

      {/* Intestazione categorie */}
      <div className="mt-3">
        <Eyebrow>Una gamma specialistica</Eyebrow>
        <h3 className="mt-1 font-serif text-[16px] font-bold text-navy">Soluzioni coordinate per esigenze differenti.</h3>
        <p className="mt-1 text-[9px] leading-[1.45] text-muted-foreground">L&apos;offerta SCHIEBLER comprende quattro famiglie principali, sviluppate per accompagnare differenti modalità di trattamento, applicazione e utilizzo quotidiano.</p>
      </div>

      {/* Griglia 2×2 categorie */}
      <div className="mt-3 grid grid-cols-2 gap-3">
        {SCHIEBLER_FAMILIES.map((f) => (
          <div key={f.title} className="flex overflow-hidden rounded-xl border border-[#E7EDF2]">
            <img src={f.img} alt={f.title} className="h-full w-[32mm] shrink-0 object-cover" />
            <div className="flex flex-1 flex-col justify-center px-4 py-3">
              <div className="font-serif text-[12.5px] font-bold leading-[1.2] text-navy">{f.title}</div>
              <p className="mt-1 text-[8.5px] leading-[1.4] text-muted-foreground">{f.desc}</p>
              {f.brands && <div className="mt-1.5 text-[7.5px] font-bold uppercase tracking-[0.1em] text-green-deep">{f.brands}</div>}
            </div>
          </div>
        ))}
      </div>

      {/* Indicatori istituzionali */}
      <div className="mt-3 grid grid-cols-3 gap-3">
        {SCHIEBLER_VALUES.map((v) => (
          <div key={v} className="flex items-center gap-2.5 rounded-xl border border-[#E7EDF2] px-4 py-2.5">
            <Check className="h-4 w-4 shrink-0 text-green" strokeWidth={2.5} />
            <span className="text-[9px] font-semibold leading-[1.2] text-navy">{v}</span>
          </div>
        ))}
      </div>

      {/* Blocco RAL separato */}
      <div className="mt-auto flex items-center gap-6 rounded-2xl border border-[#E7EDF2] bg-cloud px-7 py-3.5">
        <div className="shrink-0 rounded-xl border border-[#E7EDF2] bg-white p-2.5">
          <img src="/brands/ral.webp" alt="Marchio di qualità RAL" className="h-[17mm] w-auto object-contain" />
        </div>
        <div className="flex-1">
          <span className="inline-flex items-center rounded-full bg-green px-3.5 py-1 text-[9px] font-bold uppercase tracking-[0.16em] text-white">Qualità certificata</span>
          <h3 className="mt-2 font-serif text-[16px] font-bold text-navy">Il marchio di qualità RAL</h3>
          <p className="mt-1.5 text-[9px] leading-[1.5] text-muted-foreground">{RAL_TEXT}</p>
        </div>
      </div>
      <Foot n={9} />
    </section>

    {/* 10 — BENDAGGI */}
    <section className="sheet">
      <Head label="Bendaggi" />
      <Eyebrow>A completamento della terapia</Eyebrow>
      <Title className="mt-3 text-[24px]">Cinque famiglie di bendaggi,<br />per esigenze differenti.</Title>
      <Accent />
      <div className="mt-4 space-y-1.5 text-[9.5px] leading-[1.5] text-muted-foreground">
        <p>Il bendaggio rappresenta un elemento importante in differenti percorsi terapeutici e assistenziali. Struttura, elasticità, capacità adesiva e composizione devono essere individuate in base alla finalità di utilizzo, alla fase del trattamento e alle indicazioni ricevute.</p>
        <p>La gamma MEDISISTEM comprende cinque famiglie di bendaggi: adesivi, coesivi, non adesivi, all&apos;ossido di zinco e all&apos;alginato. Soluzioni differenti per supportare la terapia compressiva, il fissaggio delle medicazioni, la fase di decongestione e la gestione delle ferite essudanti.</p>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3.5">
        {BENDAGGI_FAMILIES.slice(0, 3).map((f) => <BendaggioCard key={f.title} f={f} />)}
      </div>
      <div className="mt-3.5 grid grid-cols-2 gap-3.5">
        {BENDAGGI_FAMILIES.slice(3, 5).map((f) => <BendaggioCard key={f.title} f={f} />)}
      </div>

      <div className="mt-auto rounded-2xl border border-green/20 bg-green/5 px-7 py-4">
        <span className="inline-flex items-center rounded-full bg-green px-3.5 py-1 text-[9px] font-bold uppercase tracking-[0.16em] text-white">Nota</span>
        <p className="mt-2 text-[9.5px] leading-[1.55] text-navy/80">{BENDAGGI_NOTE}</p>
      </div>
      <Foot n={10} />
    </section>

    {/* 11 — PRESSOTERAPIA */}
    <section className="sheet">
      <Head label="Pressoterapia" />
      <Eyebrow>Drenaggio linfatico sequenziale</Eyebrow>
      <Title className="mt-3 text-[24px]">Pressoterapia elettronica ADVANCE.</Title>
      <p className="mt-2 text-[11px] font-semibold text-navy/70">Sistema a compressione sequenziale con quattro uscite e regolazione elettronica.</p>
      <Accent />

      {/* Parte superiore — foto + descrizione/spec */}
      <div className="mt-4 grid grid-cols-2 gap-6">
        <div className="flex flex-col">
          <div className="overflow-hidden rounded-2xl border border-[#E7EDF2]">
            <img src={IMG.pressotherapy} alt="Pressoterapia elettronica ADVANCE" className="h-[62mm] w-full object-cover" />
          </div>
          <div className="mt-3 rounded-xl border border-[#E7EDF2] bg-cloud px-4 py-3">
            <p className="text-[9px] leading-[1.45] text-navy/80"><span className="font-bold text-navy">Configurazioni disponibili:</span> gambali, bracciali, fascia addominale e relativi accessori.</p>
          </div>
        </div>
        <div className="flex flex-col">
          <p className="text-[9.5px] leading-[1.5] text-muted-foreground">ADVANCE è un sistema di pressoterapia elettronica progettato per eseguire trattamenti a compressione sequenziale. Le camere presenti negli applicatori si gonfiano e si sgonfiano secondo una sequenza programmata, procedendo dalla zona distale verso quella prossimale. Attraverso il display è possibile regolare la pressione e gestire le sezioni secondo le indicazioni ricevute.</p>
          <div className="mt-4 grid flex-1 grid-cols-2 gap-3">
            {PRESSO_SPECS.map((s) => (
              <div key={s.title} className="flex flex-col justify-center rounded-xl border border-[#E7EDF2] p-4">
                <s.icon className="h-5 w-5 text-green" strokeWidth={1.75} />
                <div className="mt-1.5 font-serif text-[12px] font-bold leading-[1.15] text-navy">{s.title}</div>
                <p className="mt-0.5 text-[8.5px] leading-[1.35] text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Parte centrale — come funziona + ambiti */}
      <div className="mt-5 grid grid-cols-2 gap-6">
        <div className="flex flex-col rounded-2xl bg-navy px-6 py-5 text-white">
          <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-green">Come funziona</span>
          <h3 className="mt-2 font-serif text-[15px] font-bold leading-[1.2]">Drenaggio sequenziale, programmi A e B.</h3>
          <p className="mt-2 text-[9px] leading-[1.55] text-white/75">Durante il trattamento, le camere degli applicatori si gonfiano e si sgonfiano in successione. La compressione procede dalla zona distale verso quelle successive, accompagnando il movimento dei fluidi. Attraverso il display è possibile regolare la pressione e, quando previsto, escludere determinate sezioni.</p>
        </div>
        <div className="flex flex-col rounded-2xl border border-[#E7EDF2] px-6 py-5">
          <h3 className="font-serif text-[15px] font-bold leading-[1.2] text-navy">Quando può essere indicata la pressoterapia.</h3>
          <p className="mt-1.5 text-[9px] leading-[1.4] text-muted-foreground">Tra gli ambiti nei quali può essere prevista, previa valutazione del personale sanitario, rientrano:</p>
          <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5">
            {PRESSO_AMBITI.map((a) => (
              <div key={a} className="flex items-start gap-1.5">
                <Check className="mt-[1px] h-3.5 w-3.5 shrink-0 text-green" strokeWidth={2.5} />
                <span className="text-[9px] font-semibold leading-[1.25] text-navy">{a}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Nota sanitaria */}
      <div className="mt-auto rounded-2xl bg-navy px-7 py-4 text-white">
        <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-green">Nota sanitaria</span>
        <p className="mt-2 text-[9.5px] leading-[1.55] text-white/80">La scelta del programma, della pressione e della configurazione deve essere effettuata sulla base delle indicazioni ricevute. L&apos;utilizzo del dispositivo non sostituisce la valutazione medica e richiede la preventiva verifica di eventuali controindicazioni.</p>
      </div>
      <Foot n={11} />
    </section>

    {/* 12 — SERVIZI */}
    <section className="sheet">
      <Head label="I nostri servizi" />
      <Eyebrow>Non solo prodotti</Eyebrow>
      <Title className="mt-3 text-[24px]">Un servizio completo,<br />dalla consulenza alla consegna.</Title>
      <p className="mt-2 text-[11px] font-semibold text-navy/70">Competenza specialistica, disponibilità di prodotto e organizzazione al servizio di ogni canale.</p>
      <Accent />

      <div className="mt-4 space-y-1.5 text-[9.5px] leading-[1.5] text-muted-foreground">
        <p>MEDISISTEM integra competenza nel settore elasto-compressivo e capacità organizzativa, accompagnando la persona e gli operatori professionali dalla scelta del dispositivo alla gestione della fornitura.</p>
        <p>Il servizio comprende supporto specialistico, misurazione e prova, gestione delle disponibilità, organizzazione logistica, attenzione alla sicurezza dei prodotti e assistenza dedicata ai differenti canali serviti.</p>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4">
        {SERVICES_PROFILE.map((s) => (
          <div key={s.title} className="flex items-start gap-4 rounded-2xl border border-[#E7EDF2] p-5">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green/10">
              <s.icon className="h-[22px] w-[22px] text-green" strokeWidth={1.75} />
            </div>
            <div>
              <div className="font-serif text-[13.5px] font-bold leading-[1.2] text-navy">{s.title}</div>
              <p className="mt-1.5 text-[9.5px] leading-[1.5] text-muted-foreground">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-auto">
        <div className="grid grid-cols-3 gap-3">
          {SERVICES_INDICATORS.map((i) => (
            <div key={i.label} className="flex items-center gap-2.5 rounded-xl border border-[#E7EDF2] px-4 py-3">
              <i.icon className="h-4 w-4 shrink-0 text-green" strokeWidth={2} />
              <span className="text-[9.5px] font-semibold leading-[1.2] text-navy">{i.label}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-2xl bg-navy px-7 py-5 text-white">
          <p className="font-serif text-[14px] font-semibold leading-[1.45]">Consulenza, disponibilità, consegna e assistenza: MEDISISTEM rappresenta un unico punto di riferimento per la gestione completa della fornitura.</p>
        </div>
      </div>
      <Foot n={12} />
      <Foot n={12} />
    </section>

    {/* 13 — QUALITÀ CERTIFICATA */}
    <section className="sheet">
      <Head label="Qualità certificata" />
      <Eyebrow>Qualità certificata</Eyebrow>
      <Title className="mt-3 text-[26px]">Un impegno organizzato e verificabile.</Title>
      <p className="mt-2 text-[11px] font-semibold text-navy/70">Un sistema di gestione fondato sul controllo dei processi, sull&apos;attenzione al cliente e sul miglioramento continuo.</p>
      <Accent />

      <div className="mt-5 flex items-center gap-6 rounded-2xl border border-green/20 bg-green/5 px-8 py-6">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white ring-1 ring-green/20">
          <BadgeCheck className="h-7 w-7 text-green" strokeWidth={1.75} />
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-green-deep">Sistema di gestione per la qualità</div>
          <div className="mt-1 font-serif text-[26px] font-bold leading-none text-navy">{CERT_STANDARD}</div>
          <p className="mt-2 text-[9.5px] leading-[1.4] text-navy/60">Il sistema di gestione per la qualità di MEDISISTEM è conforme alla norma UNI EN ISO 9001:2015.</p>
        </div>
      </div>

      <div className="mt-5 space-y-2 text-[10.5px] leading-[1.6] text-muted-foreground">
        <p>MEDISISTEM adotta un sistema di gestione per la qualità conforme alla norma UNI EN ISO 9001:2015. Un modello organizzativo fondato sulla definizione delle responsabilità, sul controllo dei processi, sull&apos;attenzione alle esigenze del cliente e sul miglioramento continuo.</p>
        <p>La certificazione rappresenta l&apos;impegno dell&apos;azienda a operare attraverso procedure definite, verifiche interne e attività di monitoraggio. Le richieste ricevute, la gestione delle forniture e il supporto offerto vengono organizzati con l&apos;obiettivo di garantire affidabilità, continuità e qualità del servizio.</p>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-4">
        {CERT_PILLARS.map((p) => (
          <div key={p.title} className="rounded-2xl border border-[#E7EDF2] p-6">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-green/10">
              <p.icon className="h-[22px] w-[22px] text-green" strokeWidth={1.75} />
            </div>
            <div className="mt-3 font-serif text-[14px] font-bold leading-[1.2] text-navy">{p.title}</div>
            <p className="mt-2 text-[9.5px] leading-[1.55] text-muted-foreground">{p.desc}</p>
          </div>
        ))}
      </div>

      <div className="mt-auto rounded-2xl bg-navy px-8 py-6 text-white">
        <p className="font-serif text-[15px] font-semibold leading-[1.45]">La qualità, per MEDISISTEM, non è soltanto una dichiarazione, ma un metodo di lavoro fondato su procedure, responsabilità, controlli e miglioramento continuo.</p>
        <p className="mt-3 text-[9px] leading-[1.5] text-white/60">La certificazione UNI EN ISO 9001:2015 riguarda il sistema di gestione per la qualità di MEDISISTEM e non costituisce una certificazione automatica dei singoli prodotti distribuiti.</p>
      </div>
      <Foot n={13} />
    </section>

    {/* 14 — A CHI CI RIVOLGIAMO */}
    <section className="sheet">
      <Head label="A chi ci rivolgiamo" />
      <Eyebrow>I nostri interlocutori</Eyebrow>
      <Title className="mt-3 text-[26px]">Un riferimento per ogni canale.</Title>
      <p className="mt-2 text-[11px] font-semibold text-navy/70">Soluzioni e servizi dedicati a professionisti, strutture, distributori e pazienti.</p>
      <Accent />

      <div className="mt-4 space-y-1.5 text-[9.5px] leading-[1.5] text-muted-foreground">
        <p>MEDISISTEM lavora con una rete articolata di interlocutori: distributori farmaceutici, farmacie, sanitarie e ortopedie, medici specialisti, strutture sanitarie e pazienti. Ogni canale presenta esigenze differenti in termini di disponibilità dei prodotti, informazione tecnica, misurazione, fornitura e assistenza.</p>
        <p>Per questo adottiamo modalità di servizio dedicate, mantenendo per tutti lo stesso standard di competenza, affidabilità e continuità. Dalla gestione organizzata delle forniture al supporto diretto alla persona, accompagniamo ogni interlocutore con attenzione e professionalità.</p>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4">
        {CHANNELS_PROFILE.map((c) => (
          <div key={c.title} className="rounded-2xl border border-[#E7EDF2] p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-green/10">
                <c.icon className="h-5 w-5 text-green" strokeWidth={1.75} />
              </div>
              <div className="font-serif text-[14px] font-bold leading-[1.15] text-navy">{c.title}</div>
            </div>
            <p className="mt-2.5 text-[9.5px] leading-[1.5] text-muted-foreground">{c.desc}</p>
          </div>
        ))}
      </div>

      <div className="mt-auto rounded-2xl bg-navy px-8 py-5 text-white">
        <p className="font-serif text-[14px] font-semibold leading-[1.45]">Dalla distribuzione farmaceutica al supporto diretto alla persona, MEDISISTEM mette competenza, organizzazione e continuità al servizio dell&apos;intera filiera della terapia elasto-compressiva.</p>
      </div>
      <Foot n={14} />
    </section>

    {/* 15 — CONTATTI */}
    <section className="sheet">
      <Head label="Contatti" />
      <Eyebrow>Sede e contatti</Eyebrow>
      <Title className="mt-3 text-[26px]">MEDISISTEM, nel cuore di Napoli.</Title>
      <p className="mt-2 text-[11px] font-semibold text-navy/70">Un punto di riferimento per informazioni, forniture e assistenza.</p>
      <Accent />

      <p className="mt-4 text-[9.5px] leading-[1.5] text-muted-foreground">MEDISISTEM opera dalla propria sede nel centro di Napoli e, attraverso i diversi canali professionali, offre informazione tecnica, fornitura e assistenza. Per qualsiasi esigenza, i nostri recapiti sono a disposizione.</p>

      <div className="mt-5 grid grid-cols-2 gap-5">
        {/* Localizzazione (predisposta per futura foto reale) */}
        <div className="flex flex-col justify-center rounded-2xl border border-[#E7EDF2] bg-cloud p-8">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white ring-1 ring-green/20">
            <MapPin className="h-7 w-7 text-green" strokeWidth={1.75} />
          </div>
          <div className="mt-5 text-[10px] font-semibold uppercase tracking-[0.2em] text-green-deep">La nostra sede</div>
          <div className="mt-2 font-serif text-[25px] font-bold leading-[1.15] text-navy">Via Giuseppe Bonito 19</div>
          <div className="font-serif text-[25px] font-bold leading-[1.15] text-navy/70">Napoli</div>
          <div className="mt-5 h-1 w-12 rounded-full bg-green" />
        </div>

        {/* Recapiti */}
        <div className="flex flex-col justify-between gap-4">
          {[
            { icon: Phone, label: "Telefono", value: "081 7433033 · 081 5920490" },
            { icon: WhatsappIcon, label: "WhatsApp Business", value: "339 1424138" },
            { icon: Mail, label: "Email", value: "info@medisistem.it" },
            { icon: Globe, label: "Sito web", value: "www.medisistem.it" },
          ].map((c) => (
            <div key={c.label} className="flex flex-1 items-center gap-4 rounded-2xl border border-[#E7EDF2] px-5 py-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green/10">
                <c.icon className="h-5 w-5 text-green" strokeWidth={1.75} />
              </div>
              <div>
                <div className="text-[9px] font-semibold uppercase tracking-[0.15em] text-navy/40">{c.label}</div>
                <div className="mt-0.5 text-[13.5px] font-semibold text-navy">{c.value}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 min-h-[42mm] flex-1 overflow-hidden rounded-2xl border border-[#E7EDF2]">
        <img src="https://images.unsplash.com/photo-1710625361134-332bc2801df3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxOYXBsZXMlMjBndWxmJTIwVmVzdXZpdXMlMjBwYW5vcmFtYSUyMGVsZWdhbnR8ZW58MHx8fHwxNzg3ODI3NjI0fDA&ixlib=rb-4.1.0&q=85" alt="Napoli, il golfo e il Vesuvio" className="h-full w-full object-cover" />
      </div>

      <div className="mt-5">
        <p className="text-[8.5px] leading-[1.4] text-navy/40">MEDISISTEM S.n.c. di Mario De Peppo &amp; C. · P. IVA 06546510634</p>
        <div className="mt-3 rounded-2xl bg-navy px-8 py-6 text-white">
          <p className="text-[11.5px] leading-[1.55] text-white/85">In sede a Napoli e attraverso i diversi canali professionali, MEDISISTEM è a disposizione per informazioni, forniture e assistenza.</p>
          <div className="mt-4 flex items-center gap-3">
            <span className="h-px w-12 bg-green" />
            <span className="font-serif text-[21px] font-bold">Affidabilità che si indossa.</span>
          </div>
        </div>
      </div>
      <Foot n={15} />
    </section>
  </div>
);

export default CompanyProfilePrint;
